import { useState } from 'react';
import { useRequiredParams } from 'app/router/params';
import { useEditorContext } from 'app/state/contexts/EditorContext';
import { useDiff } from 'app/state/contexts/DiffContext';

import { useRevisionL, useRevisionR } from './useRevision';
import { useParagraphHistory } from 'entities/document';
import Accordion from 'react-bootstrap/Accordion';
import Sidebar from './Sidebar';
import moment from 'moment';

interface TableCell {
  rawData?: { w?: string };
  [key: string]: unknown;
}

interface DiffEntry {
  type?: string;
  state?: string;
  computedDiff?: unknown;
  text?: string;
  data?: unknown;
  offset?: number;
  index?: number | null;
  key?: string;
}

interface DiffState {
  left?: Record<string, DiffEntry>;
  right: Record<string, DiffEntry>;
  leftSource?: Record<string, unknown>;
  rightSource?: Record<string, unknown>;
}

type ChangeItem = DiffEntry & { index: number | null; key: string; text: string };

interface UserHistoryEntry {
  user: { userName: string };
  lastChanged: string;
}

interface ChangeProps {
  change: ChangeItem;
  select: (changeKey: string, index: number | null) => void;
  accordionIdx: number;
  changeKey: string;
  selected: boolean;
  userHistory: UserHistoryEntry[];
  diffState: DiffState;
}

const Change = ({ change, select, accordionIdx, changeKey, selected, userHistory, diffState }: ChangeProps) => {
  const oppositeChangeBase = diffState?.rightSource?.[changeKey] as DiffEntry | undefined;
  if (!oppositeChangeBase) {
    return null;
  }

  let opposingSideText;
  switch (change.type) {
    case 'table':
      opposingSideText = tableText(oppositeChangeBase.data);
      break;
    case 'list-item':
      opposingSideText = (oppositeChangeBase.data as string[]).join('');
      break;
    default:
      opposingSideText = oppositeChangeBase.text;
  }

  return (
    <Accordion.Item eventKey={String(accordionIdx)}>
      <Accordion.Header onClick={() => select(changeKey, change.state === 'missing' ? null : change.index)}>
        <InfoCircle type={change.type} />
        <div className="pt-1 ps-3">
          <div>
            {change.state === 'changed' ? truncate(change.text, 45) : change.state === 'missing' ? truncate(opposingSideText, 45) : 'Sida ' + change.offset}
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Body className="pt-0">
        <div className="fs-9 ms-5 text-gray-700">
          {selected
            ? userHistory.map(({ user, lastChanged }) => (
                <div key={user.userName} className="ms-2">
                  {user.userName}, {moment(lastChanged).format('YYYY-MM-DD HH:mm')}
                </div>
              ))
            : null}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
};

const InfoCircle = ({ type }: { type?: string }) => {
  let icon = null;
  switch (type) {
    case 'text':
      icon = <i className="fa-regular fa-text"></i>;
      break;
    case 'table':
      icon = <i className="fa-regular fa-table"></i>;
      break;
    case 'numbered-list':
    case 'bulleted-list':
    case 'list-item':
      icon = <i className="fa-regular fa-list"></i>;
      break;
    default:
      icon = <i className="fa-regular fa-question"></i>;
  }

  return <div className="user--circle text-blue-700">{icon}</div>;
};

const truncate = (str: string | undefined, n: number) => {
  if (!str || !str?.length) {
    return '';
  }
  return str.length > n ? str.substr(0, n - 1) + '…' : str;
};

type Diff = [number, string];

const getTextFromDiff = (computedDiff: unknown): string[] => {
  if (!computedDiff || !Array.isArray(computedDiff)) {
    return [];
  }

  if (Array.isArray((computedDiff[0] as unknown[] | undefined)?.[0])) {
    const table = computedDiff as Diff[][][];
    const tableDiff = table
      .flatMap((row) => row)
      .filter(([[diffType]]) => diffType !== 0)
      .map((cellDiffs) => cellDiffs.map(([, text]) => text).join(''))
      .map((cellContent) => `[${cellContent}]`)
      .join(', ');

    return [tableDiff];
  }
  return (computedDiff as Diff[]).filter(([type]) => type === -1).map(([, text]) => text);
};

const getSelectedSection = (shadowHost: { shadowRoot?: ShadowRoot | null } | null | undefined, nodeId: string, index: number | null) => {
  const element = shadowHost?.shadowRoot?.querySelector(`#anchor-${nodeId}`);
  const changes = element?.parentElement?.querySelectorAll('.diffed-change-added');
  if (!changes) {
    return null;
  }
  if (changes.length === 0) {
    // Entire paragraph is assumed as changed
    return element?.parentElement;
  }

  return changes[index ?? 0];
};

const calculateChanges = (diffState: DiffState): ChangeItem[] => {
  let diffStateEntries = Object.entries(diffState.right);
  if (diffState.left) {
    const leftSide = Object.entries(diffState.left).filter(([, value]) => value?.type === 'page');
    diffStateEntries = diffStateEntries.concat(leftSide);
  }

  return diffStateEntries
    .filter((it) => Array.isArray(it))
    .flatMap(([key, entry]) => {
      const diffTexts = getTextFromDiff(entry.computedDiff);
      return diffTexts.filter((text) => text?.trim()).map((text, index) => ({ index: diffTexts.length === 1 ? null : index, key, text, ...entry }));
    });
};

export const ParagraphChanges = () => {
  const { diffState } = useDiff();
  const { shadowHost } = useEditorContext();
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const { documentId, sectionId } = useRequiredParams('documentId', 'sectionId');
  const [revisionL] = useRevisionL();
  const [revisionR] = useRevisionR();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const { data: userHistory = [] } = useParagraphHistory(documentId, sectionId, revisionL, revisionR, selectedNodeId) as {
    data: UserHistoryEntry[] | undefined;
  };

  const removeSelection = () => {
    const element = shadowHost?.shadowRoot?.querySelector(`.diffed-change-selected`);
    element?.classList?.remove('diffed-change-selected');
    element?.classList?.add('diffed-change-added');

    setFocusedKey(null);
    setFocusedIndex(null);
  };

  const toggleSelected = (nodeId: string, index: number | null) => {
    if (focusedKey === nodeId && focusedIndex === index) {
      removeSelection();
      return;
    }
    removeSelection();
    const innerElement = getSelectedSection(shadowHost, nodeId, index);
    if (innerElement) {
      innerElement.classList.add('diffed-change-selected');
      innerElement.classList.remove('diffed-change-added');
      setFocusedKey(nodeId);
      setFocusedIndex(index);
      const y = innerElement.getBoundingClientRect().top + window.pageYOffset - 160;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      setFocusedKey(null);
      setFocusedIndex(null);
    }
  };

  const selectChange = (nodeId: string, index: number | null) => {
    setSelectedNodeId(nodeId);
    toggleSelected(nodeId, index);
  };

  if (!diffState?.right) {
    return null;
  }
  const changes = calculateChanges(diffState);

  const isSelected = (key: string, index: number | null) => {
    if (focusedKey === key && (focusedIndex === null || focusedIndex === index)) {
      return true;
    }

    return false;
  };

  const getSelectedIndex = () => {
    return changes.findIndex((change) => change.key === focusedKey && change.index === focusedIndex);
  };

  return (
    <div>
      <Sidebar.Header>
        <div className="fw-bold">Ändringar</div>
        <div className="text-gray-800 fs-7">
          {getSelectedIndex() + 1}/{changes.length}
        </div>
      </Sidebar.Header>
      <Accordion flush>
        {changes.map((change, idx) => (
          <Change
            key={idx}
            accordionIdx={idx}
            change={change}
            changeKey={change.key}
            selected={isSelected(change.key, change.index)}
            userHistory={userHistory}
            select={selectChange}
            diffState={diffState}
          />
        ))}
      </Accordion>
    </div>
  );
};

const tableText = (data: unknown) => {
  return (data as TableCell[][] | undefined)
    ?.flatMap((row) => row.map((col) => col.rawData?.w))
    .map((cell) => `[${cell}]`)
    .join(', ');
};
